import { createRouter, createWebHistory } from "vue-router";
import sessionService from "../services/session";
import navigationGuardService from "../services/navigationGuard";
import { useAuthStore } from "../stores/auth";
import { applyLoginScreenLocale, shouldApplyGuestLocale } from "@/utils/appLocale";

import LoginView from "../views/LoginView.vue";
import { defaultSidebarNav } from "../config/sidebarNav";
import { defaultBrand } from "../config/brands";

import RememberPasswordView from "../views/password/RememberPasswordView.vue";
import ReviewPasswordView from "../views/password/ReviewPasswordView.vue";
import NewPasswordView from "../views/password/NewPasswordView.vue";

/**
 * TODO: Implementar 404 https://v3.router.vuejs.org/guide/essentials/history-mode.html#example-server-configurations
 *
 * TODO: Crear childrens: https://v3.router.vuejs.org/guide/essentials/nested-routes.html
 */

const routes = [
  {
    path: "/",
    name: "home",
    redirect: "/dashboard",
    meta: { guest: true },
  },
  {
    path: "/login",
    name: "login",
    component: LoginView,
    meta: { guest: true },
  },
  {
    path: "/remember-password",
    name: "remember-password",
    component: RememberPasswordView,
    meta: { guest: true },
  },

  {
    path: "/new-password",
    name: "new-password",
    component: NewPasswordView,
    meta: { guest: true },
  },

  {
    path: "/review-password",
    name: "review-password",
    component: ReviewPasswordView,
    meta: { guest: true },
  },

  {
    path: "/dashboard",
    name: "dashboard",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import("../views/DashboardView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/logout",
    name: "logout",
    component: () => import("../views/LogoutView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/notificaciones",
    component: () => import("../views/NotificationView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/marketing/pedir-material",
    name: "marketing/pedir-material",
    component: () => import("../views/marketing/MarketingOrderView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/marketing/checkout",
    name: "marketing/checkout",
    component: () => import("../views/marketing/CheckoutView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/marketing",
    name: "marketing",
    // Redirect to the new themed marketing route
    redirect: "/theme/indo/marketing",
    meta: { requiresAuth: true },
  },

  {
    path: "/reparacion",
    name: "reparacion",
    component: () => import("../views/RepareView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/consumibles",
    name: "consumibles",
    component: () => import("../views/ConsumablesView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/bienes-de-equipo",
    name: "bienes-de-equipo",
    component: () => import("../views/CapitalGoods.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/descargas",
    name: "descargas",
    component: () => import("../views/Downloads.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/notification/detail/:noticia",
    name: "notification-detail",
    props: true,
    component: () => import("../views/notification/NotificationDetailView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/indomanager",
    name: "facturacion",
    // Redirect to the new themed billing route
    redirect: "/theme/indo/facturacion/facturas-y-albaranes",
    meta: { requiresAuth: true },
  },

  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: () => import("../views/legal/PrivacyPolicyView.vue"),
    meta: { requiresAuth: false },
  },

  {
    path: "/legal-notice",
    name: "legal-notice",
    component: () => import("../views/legal/LegalNoticeView.vue"),
    meta: { requiresAuth: false },
  },

  {
    path: "/cookies-policy",
    name: "cookies-policy",
    component: () => import("../views/legal/CookiesPolicyView.vue"),
    meta: { requiresAuth: false },
  },

  {
    path: "/pedir-lentes/datos-basicos",
    name: "pedir-lentes",
    component: () => import("../views/orderLenses/OrderLensesView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/pedir-lentes/formas-espesores",
    name: "formas-espesores",
    component: () => import("../views/orderLenses/FormsThicknessesView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/pedir-lentes/enviar-pedido",
    name: "enviar-pedido",
    component: () => import("../views/orderLenses/SendOrderView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/formacion",
    name: "formacion",
    // Redirect to the new themed formation route
    redirect: "/theme/indo/formacion",
    meta: { requiresAuth: true },
  },

  {
    path: "/lentes/pedidos",
    name: "pedidos",
    component: () => import("../views/lenses/OrdersView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/lentes/devoluciones",
    name: "devoluciones",
    component: () => import("../views/lenses/ReturnsView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/lentes/recogidas",
    name: "recogidas",
    component: () => import("../views/lenses/RecogidasView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/lentes/pedidos/analizar",
    name: "analizar",
    component: () => import("../views/lenses/LensesAnalize.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/lentes/recetas-guardadas",
    name: "recetas-guardadas",
    component: () => import("../views/lenses/RecipesView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/indomanager/informacion-fiscal",
    name: "informacion-fiscal",
    component: () => import("../views/facturation/FiscalInformationView.vue"),
    meta: { requiresAuth: true, requiresManager: true },
  },

  {
    path: "/indomanager/stats",
    name: "informacion-servicio",
    component: () => import("../views/facturation/ServiceInformationView.vue"),
    meta: { requiresAuth: true, requiresManager: true },
  },

  {
    path: "/indomanager/bonos",
    name: "bonos",
    component: () => import("../views/facturation/BonosView.vue"),
    meta: { requiresAuth: true, requiresManager: true },
  },

  {
    path: "/indomanager/acuerdos-comerciales",
    name: "acuerdos-comerciales",
    component: () => import("../views/facturation/CommercialsAgreements.vue"),
    meta: { requiresAuth: true, requiresManager: true },
  },

  {
    path: "/indomanager/facturas-y-albaranes",
    name: "facturas-y-albaranes",
    component: () => import("../views/facturation/InvoicesDeliveryNotesView.vue"),
    meta: { requiresAuth: true, requiresManager: true },
  },

  {
    path: "/indomanager/facturas-y-albaranes/abrir-consulta/:item",
    name: "abrir-consulta",
    props: { item: true },
    component: () => import("../views/facturation/ConsultingFormView.vue"),
    meta: { requiresAuth: true, requiresManager: true },
  },
  {
    path: "/myprofile",
    name: "myprofile",
    component: () => import("../views/MyProfileView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/settings/optic-data",
    name: "optic-data",
    component: () => import("../views/settings/OpticDataView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/settings/websearch",
    name: "websearch",
    component: () => import("../views/settings/WebsearchView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/settings/mailbox",
    name: "mailbox",
    component: () => import("../views/settings/MailboxView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/settings/calibration-monitor",
    name: "calibration-monitor",
    component: () => import("../views/settings/CalibrationMonitorView.vue"),
    meta: { requiresAuth: true },
  },

  /*   {
    path: "/settings/plotters",
    name: "plotters",
    component: () => import("../views/settings/PlottersView.vue"),
    meta: { requiresAuth: true },
  }, */

  {
    path: "/settings/favorites",
    name: "favorites",
    component: () => import("../views/settings/FavoritesView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/settings/configuration-indonet",
    name: "configuration-indonet",
    component: () => import("../views/settings/ConfigurationIndonetView.vue"),
    meta: { requiresAuth: true },
  },

  // Dev pourpouses only
  // TODO: Remove this routes when the project is finished
  {
    path: "/sandbox",
    meta: { requiresAuth: true },
    children: [
      { path: "", component: () => import("../views/sandbox/IndexView.vue") },
      { path: "icons", component: () => import("../views/sandbox/IconsView.vue") },
      { path: "forms", component: () => import("../views/sandbox/FormsView.vue") },
      { path: "tables", component: () => import("../views/sandbox/TablesView.vue") },
      { path: "ui", component: () => import("../views/sandbox/UiView.vue") },
      { path: "cards", component: () => import("../views/sandbox/CardsView.vue") },
      { path: "banners", component: () => import("../views/sandbox/BannersView.vue") },
      { path: "charts", component: () => import("../views/sandbox/ChartsView.vue") },
      { path: "modal", component: () => import("../views/sandbox/ModalView.vue") },
      { path: "flow", component: () => import("../views/sandbox/FlowView.vue") },
    ],
  },

  /// Pages tree

  {
    path: "/theme",
    component: () => import("../components/theme/layout/app/AppLayout.vue"),
    meta: { requiresAuth: true },
    props: (route) => {
      const requiresBrandAuth = route.matched.some((r) => r.meta.requiresBrandAuth);
      const brandUser =
        typeof localStorage !== "undefined" ? localStorage.getItem("brandUser") : null;
      return {
        scrollable: true,
        nav: defaultSidebarNav,
        brand: route.params.brand ?? defaultBrand,
        brandUser: requiresBrandAuth ? brandUser : null,
        useNestedRouterView: true,
      };
    },
    children: [
      {
        path: ":brand",
        beforeEnter: (to, from, next) => {
          // Validaci?n din?mica: aceptar cualquier brand con caracteres alfanum?ricos
          const brand = to.params.brand;
          const isValidBrand = /^[a-z0-9]+$/.test(brand);

          if (isValidBrand) {
            next();
          } else {
            next({ path: "/dashboard" });
          }
        },
        children: [
          {
            path: "facturacion",
            component: () => import("../pages/60-billing/BillingView.vue"),
            meta: { requiresBrandAuth: true },
            beforeEnter: (to, from, next) => {
              // Verificar autenticaci?n desde Vuex antes de entrar a cualquier ruta de facturaci?n
              const isAuthManager = useAuthStore().isAuthManager;

              // Si no est? autenticado y no va al login, redirigir al login
              if (!isAuthManager && to.name !== "theme-billing-login") {
                next({
                  name: "theme-billing-login",
                  params: { brand: to.params.brand },
                });
              } else {
                // Si est? autenticado o va al login, permitir acceso
                next();
              }
            },
            children: [
              {
                path: "",
                redirect: (to) => {
                  // Si no est? autenticado, redirigir al login
                  const isAuthManager = useAuthStore().isAuthManager;
                  if (!isAuthManager) {
                    return {
                      name: "theme-billing-login",
                      params: { brand: to.params.brand },
                    };
                  }
                  // Si est? autenticado, ir a facturas
                  return {
                    name: "theme-invoices-delivery-notes",
                    params: { brand: to.params.brand },
                  };
                },
              },
              {
                path: "login",
                name: "theme-billing-login",
                component: () => import("../pages/60-billing/00-login/BrandLoginView.vue"),
              },
              {
                path: "facturas-y-albaranes",
                name: "theme-invoices-delivery-notes",
                component: () =>
                  import("../pages/60-billing/10-invoices-delivery-notes/InvoicesDeliveryNotesView.vue"),
              },
              {
                path: "bonos",
                children: [
                  {
                    path: "",
                    name: "theme-bonus",
                    component: () =>
                      import("../pages/60-billing/20-bonus/10-bonus-list/BonusListView.vue"),
                  },
                  {
                    path: ":bonusId",
                    name: "theme-bonus-detail",
                    component: () =>
                      import("../pages/60-billing/20-bonus/20-bonus-detail/BonusDetailView.vue"),
                  },
                ],
              },
              {
                path: "acuerdos-comerciales",
                name: "theme-commercials-agreements",
                component: () =>
                  import("../pages/60-billing/30-commercials-agreements/CommercialsAgreements.vue"),
              },
              {
                path: "estadisticas",
                name: "theme-stats",
                component: () => import("../pages/60-billing/40-stats/StatsView.vue"),
              },
              {
                path: "informacion-fiscal",
                name: "theme-fiscal-information",
                component: () =>
                  import("../pages/60-billing/50-fiscal-information/FiscalInformationView.vue"),
              },
              {
                path: "cambiar-contrasena",
                name: "theme-change-password",
                component: () =>
                  import("../pages/60-billing/60-change-password/ChangePasswordView.vue"),
              },
            ],
          },
          {
            path: "formacion",
            name: "theme-formation",
            component: () => import("../pages/70-formation/FormationView.vue"),
          },
          {
            path: "marketing",
            name: "theme-marketing",
            component: () => import("../pages/80-marketing/MarketingView.vue"),
          },
        ],
      },
    ],
  },

  // {
  //   path: "/theme",
  //   component: () => import("../pages/parent/ParentView.vue"),
  //   meta: { requiresAuth: true },
  //   children: [
  //     {
  //       // UserProfile will be rendered inside User's <router-view>
  //       // when /user/:id/profile is matched
  //       path: "sub1",
  //       component: () => import("../pages/parent/Sub1View.vue"),
  //     },
  //     {
  //       // UserProfile will be rendered inside User's <router-view>
  //       // when /user/:id/profile is matched
  //       path: "sub1",
  //       component: () => import("../pages/parent/Sub1View.vue"),
  //     },
  //   ],
  // },
  {
    path: "/:pathMatch(.*)*", // Catch all 404
    component: () => import("../pages/404.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

// Reset body overflow on every navigation to prevent scroll lock from modals/overlays
router.afterEach(() => {
  document.body.style.overflow = "";
});

router.beforeEach(async (to, from, next) => {
  if (shouldApplyGuestLocale(to.path)) {
    applyLoginScreenLocale();
  }

  // Detectar si el usuario est? navegando desde una ruta de marca a una ruta fuera de marca
  const isBrandRoute = (path) => {
    // Rutas de marca: /theme/:brand/facturacion, /theme/:brand/formacion, /theme/:brand/marketing
    return /^\/theme\/[^\/]+\/facturacion/.test(path);
  };

  const isLoginRoute = (path) => {
    // Rutas de login que deben ser excluidas
    return path === "/login" || /^\/theme\/[^\/]+\/facturacion\/login/.test(path);
  };

  const authStore = useAuthStore();

  const isComingFromBrandRoute = isBrandRoute(from.path);
  const isGoingToBrandRoute = isBrandRoute(to.path);
  const isGoingToLogin = isLoginRoute(to.path);
  const isComingFromLogin = isLoginRoute(from.path);

  // Si viene de una ruta de marca y va a una ruta que NO es de marca, mostrar popup
  // EXCEPTO si va a una ruta de login
  if (isComingFromBrandRoute && !isGoingToBrandRoute && !isGoingToLogin && !isComingFromLogin) {
    const result = await navigationGuardService.showManagerLogoutAlert();

    if (result === "yes") {
      // El usuario quiere cerrar sesi?n en IndoManager
      await authStore.ManagerLogout();
      // Continuar con la navegaci?n
      next();
    } else {
      // El usuario NO quiere cerrar sesi?n, solo navegar
      next();
    }
    return;
  }

  // Verificar si el usuario est? intentando acceder a una ruta que requiere autenticaci?n
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresManager = to.matched.some((record) => record.meta.requiresManager);
  const requiresBrandAuth = to.matched.some((record) => record.meta.requiresBrandAuth);
  const isGuestRoute = to.matched.some((record) => record.meta.guest);

  // Verificar expiraci?n de sesi?n para usuarios autenticados
  if (requiresAuth && authStore.token) {
    const isExpired = await sessionService.isSessionExpired();

    if (isExpired) {
      try {
        await authStore.Logout();
      } catch (error) {
        if (authStore.token) {
          try {
            await authStore.Logout();
          } catch (retryError) {
          }
        }
      }
      if (to.path !== "/login") {
        next("/login");
      } else {
        next();
      }
      return;
    }
  }

  // Redirigir usuarios autenticados que intentan acceder a rutas de invitado (login, remember-password, etc.)
  if (isGuestRoute && authStore.token) {
    next("/dashboard");
    return;
  }

  // Manejar rutas que requieren autenticaci?n y manager
  if (requiresAuth && requiresManager) {
    if (authStore.token && authStore.isAuthManager) {
      next();
      return;
    }
    next("/indomanager");
    return;
  }

  // Manejar rutas que requieren autenticaci?n general
  if (requiresAuth && !authStore.token) {
    next("/login");
    return;
  }

  // === Guards de modo ===
  const isOptovisionMode = import.meta.env.VITE_MODO_OPTOVISION === "true";
  const isTestMode = import.meta.env.VITE_MODO_TEST === "true";
  const path = to.path;

  // Recogidas y devoluciones: ocultas SOLO cuando VITE_MODO_OPTOVISION=true.
  if (isOptovisionMode) {
    const optovisionHidden = ["/lentes/recogidas", "/lentes/devoluciones"];
    if (optovisionHidden.some((r) => path.startsWith(r))) {
      next("/dashboard");
      return;
    }
  }

  // Bienes de equipo y marketing: visibles SOLO cuando VITE_MODO_TEST=true.
  if (!isTestMode) {
    const isBienes = path.startsWith("/bienes-de-equipo");
    const isMarketing = /^\/(marketing|theme\/[^/]+\/marketing)/.test(path);
    if (isBienes || isMarketing) {
      next("/dashboard");
      return;
    }
  }

  // Manejar autenticaci?n de marca (requiresBrandAuth)
  // Solo se llega aqu? si el usuario YA est? autenticado (por requiresAuth)
  if (requiresBrandAuth) {
    const brandLoggedUser = localStorage.getItem("brandUser");
    if (brandLoggedUser) {
      next();
      return;
    }
    // Si no hay brandUser, permitir acceder a la ruta pero mostrar? el componente de login
    next();
    return;
  }

  next();
});

export default router;
